import React, { useState, useEffect, useContext } from 'react';
import { Spinner } from 'reactstrap';
import { UserContext } from '../../components/UserContext'
import Lottie from 'react-lottie';
import { default as animationData } from '../../assets/loader.json';
import { cx, css, keyframes } from '@emotion/css';
import * as assignmentStyles from '../assignments/assignments.style';
import Select from 'react-select';
import UpgradeModal from '../UpgradeModels/UpgradeModal';
import { SubscriptionContext } from '../../reducers/SubscriptionContext';
import PracticeNewCard from '../Card/PracticeNewCard';
import { useHistory, Link, useLocation } from 'react-router-dom';

function importAll(r) {
  let images = {};
  r.keys().map((item, index) => { images[item.replace('./', '')] = r(item); });
  return images;
}

const pageBgStyles = css`
  background-color: #fdfbf7;
  background-image: 
    radial-gradient(at 100% 0%, rgba(241, 102, 2, 0.04) 0px, transparent 50%),
    radial-gradient(at 0% 100%, rgba(244, 203, 14, 0.02) 0px, transparent 50%);
  min-height: 100vh;
  padding-bottom: 100px;
  overflow-x: hidden;
`;

const headerStyles = {
  container: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 1rem;
    margin-bottom: 1.5rem;
    margin-top: 1.5rem;
    border-bottom: 1px solid rgba(241, 102, 2, 0.08);
    padding-bottom: 1.2rem;

    @media (max-width: 991px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 1.2rem;
      margin-bottom: 1.5rem;
      padding-bottom: 1.2rem;
    }
  `,
  leftCol: css`
    flex: 1;
    max-width: 850px;
  `,
  topLabel: css`
    font-family: 'Inter', sans-serif;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: rgba(241, 102, 2, 0.7);
    margin-bottom: 0.6rem;
    display: block;
  `,
  heading: css`
    font-family: 'Outfit', 'Inter', sans-serif;
    font-size: 2.4rem;
    font-weight: 800;
    font-style: italic;
    line-height: 1.05;
    margin: 0 0 0.5rem 0;
    letter-spacing: -0.02em;
    text-transform: uppercase;

    .orange-part {
      color: #f16602;
      margin-right: 0.6rem;
      display: inline-block;
    }

    .outline-part {
      color: transparent;
      -webkit-text-stroke: 1.5px rgba(241, 102, 2, 0.25);
      font-weight: 900;
      display: inline-block;
    }

    @media (max-width: 768px) {
      font-size: 1.8rem;
      
      .outline-part {
        -webkit-text-stroke: 1px rgba(241, 102, 2, 0.25);
      }
    }
  `,
  description: css`
    font-family: 'Inter', sans-serif;
    font-size: 1.05rem;
    color: #554a42;
    line-height: 1.6;
    margin: 0;
    max-width: 700px;
    font-weight: 400;

    @media (max-width: 768px) {
      font-size: 0.95rem;
    }
  `,
  rightCol: css`
    display: flex;
    align-items: center;
    gap: 1.2rem;
    flex-wrap: wrap;

    @media (max-width: 991px) {
      width: 100%;
    }
  `,
  button: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1.5px solid rgba(241, 102, 2, 0.25);
    border-radius: 9999px;
    padding: 14px 28px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: #f16602;
    text-transform: uppercase;
    text-decoration: none;
    transition: all 0.3s ease;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      background: #f16602;
      border-color: #f16602;
      color: #ffffff;
      text-decoration: none;
      box-shadow: 0 6px 20px rgba(241, 102, 2, 0.15);
    }

    @media (max-width: 576px) {
      width: 100%;
      padding: 12px 24px;
    }
  `,
  selectWrapper: css`
    width: 200px;
    
    @media (max-width: 576px) {
      width: 100%;
    }
  `
};

function PracticeCatScreen() {
  const history = useHistory();
  const { user } = useContext(UserContext);
  const { subscriptionStatus } = useContext(SubscriptionContext);
  const location = useLocation();
  
  const [level, setLevel] = useState(5);
  const [events_category, set_events_category] = useState([]);
  const [loadedcategories, set_loadedcategories] = useState(false);
  const [grade_user, set_grade_user] = useState(5);
  const [premiumlevel, set_premiumlevel] = useState(0);
  const leveloptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const [showModal, setShowModal] = useState(true);

  // Load user data from context
  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      set_grade_user(user.grade || 5);
      setLevel(user.grade || 5);
      set_premiumlevel(user.premiumuser || 0);
    }
  }, [user]);

  // Load categories when grade or premium level changes
  useEffect(() => {
    loadCategories(grade_user, premiumlevel);
  }, [grade_user, premiumlevel]);

  const loadCategories = (grade, pLevel) => {
    set_loadedcategories(false); // Ensure loading state is active
    
    // Defer the heavy processing to allow the Lottie animation to render first
    setTimeout(() => {
      try {
        const quizes = importAll(require.context('./resources', true, /\.(json)$/));
        const keys = Object.keys(quizes);

        let categorySet = new Set();
        for (let i = 0; i < keys.length; i++) {
          categorySet.add(keys[i].split("/")[0]);
        }
        let categorylist = Array.from(categorySet);
        let currentGrade = `Grade${grade}`;

        let data1 = [];
        let serialnumber = 0;
        
        categorylist.forEach(category => {
          let cat_count = 0;
          keys.forEach(key => {
            const gradePattern = new RegExp(`/${currentGrade}/`);
            const isMatchingGrade = gradePattern.test(key);
            const isMatchingCategory = key.includes(category);
            
            if (isMatchingCategory && isMatchingGrade) {
              const premiumMatch = key.match(/premium(\d+)/);
              let requiredLevel = 0;
              if (premiumMatch) {
                requiredLevel = parseInt(premiumMatch[1]);
              } else if (key.includes('/classic/')) {
                requiredLevel = 100;
              }

              if (requiredLevel === 0 || pLevel >= requiredLevel) {
                cat_count++;
              }
            }
          });

          if (cat_count > 0) {
            data1.push({
              id: ++serialnumber,
              category: category,
              count: cat_count,
              level: grade,
              premium: pLevel
            });
          }
        });

        set_events_category(data1);
        set_loadedcategories(true);
      } catch (error) {
        console.error("Error loading categories:", error);
        set_loadedcategories(true);
      }
    }, 400); // 400ms delay gives enough time for the loader to mount and spin
  };

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  const handleGradeChange = (selectedGrade) => {
    set_grade_user(selectedGrade);
  };

  let event_category_Cards = events_category.map((eventcategory) => {
    let displayCategory = eventcategory.category;

    return (
      <PracticeNewCard
        key={eventcategory.id}
        title={displayCategory}
        eventcategory={eventcategory}
        premiumlevel={premiumlevel}
        target="practice"
      />
    );
  });

  return (
    <div className={cx(pageBgStyles)}>
      {(subscriptionStatus === 'expired' || subscriptionStatus === 'none') && (
        <UpgradeModal show={showModal} onClose={() => setShowModal(false)} />
      )}
      
      
      
      <div className="container" style={{ marginTop: '2.5rem' }}>
        {/* Curated Header Layout replicating the uploaded image with orange theme */}
        <div className={cx(headerStyles.container)}>
          <div className={cx(headerStyles.leftCol)}>
            <span className={cx(headerStyles.topLabel)}>PRACTICE TRACKS</span>
            <h1 className={cx(headerStyles.heading)}>
              <span className="orange-part">Curated Practice</span>
              <span className="outline-part">Experiences</span>
            </h1>
            <p className={cx(headerStyles.description)}>
              Deep-dive into specialized academic tracks crafted to empower every stage of your learning journey, from fundamentals to mastery.
            </p>
          </div>
          
          <div className={cx(headerStyles.rightCol)}>
            {/* Grade Selection Dropdown */}
            <div className={cx(headerStyles.selectWrapper)}>
              <Select
                className="custom-select"
                classNamePrefix="select"
                name="level"
                options={leveloptions.map((times) => ({
                  value: times,
                  label: `Grade ${times}`,
                }))}
                onChange={(e) => {
                  setLevel(e.value);
                  handleGradeChange(e.value);
                }}
                value={{ value: level, label: `Grade ${level}` }}
                isSearchable={false}
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    height: "48px",
                    borderRadius: "9999px",
                    border: state.isFocused ? "1.5px solid #f16602" : "1.5px solid rgba(241, 102, 2, 0.25)",
                    boxShadow: state.isFocused ? "0 0 0 4px rgba(241, 102, 2, 0.06)" : "none",
                    backgroundColor: "#fff",
                    transition: "all 0.3s ease",
                    fontWeight: "700",
                    fontSize: "0.75rem",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    paddingLeft: "10px",
                    cursor: "pointer",
                    "&:hover": { borderColor: "#f16602" },
                  }),
                  menu: (provided) => ({
                    ...provided,
                    borderRadius: "16px",
                    boxShadow: "0 10px 30px rgba(241, 102, 2, 0.08)",
                    padding: "8px",
                    zIndex: 100,
                    border: "1px solid rgba(241, 102, 2, 0.05)"
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? "#f16602" : state.isFocused ? "#fff3e0" : "transparent",
                    color: state.isSelected ? "#fff" : "#333",
                    borderRadius: "8px",
                    margin: "2px 0",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "0.85rem",
                  })
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              {/* View My Assignments Button */}
              <Link to="/assignments" className={cx(headerStyles.button)}>
                View My Assignments
              </Link>
            </div>
          </div>
        </div>
        
        {loadedcategories ? (
          event_category_Cards.length ? (
            <div className={cx(assignmentStyles.challengeGrid)} style={{ padding: '0 0 80px 0' }}>
              {event_category_Cards}
            </div>
          ) : (
            <div className="text-center py-5">
              <div className="alert alert-info" style={{ borderRadius: '16px', padding: '1.5rem', fontWeight: '500', backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffe0b2' }}>
                No Practice Events for Grade {grade_user}. Please choose another grade.
              </div>
            </div>
          )
        ) : (
          <div className="text-center py-5">
            <Lottie options={defaultOptions} height={200} width={200} />
            <p className="mt-3">Loading your practice modules...</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PracticeCatScreen;
